/**
 * src/components/shared/BackToAppButton.jsx
 *
 * Escape hatch for shell-less full-screen routes (/admin/traces,
 * /admin/observability, /reorder/*) that render OUTSIDE NavigationShell and
 * therefore have no bottom nav / no way back to the app.
 *
 * P0 fix 2026-06-11 (CEO): "once I'm in [the Trace Viewer / admin page] I can't
 * go back to the app, there is no button or arrow." Those pages are reached via
 * a full-page <a href="/admin/..."> from Settings, landing the user on a
 * navless screen. This fixed, safe-area-aware button returns them to the app.
 *
 * Presentational only (no hooks) so it unit-tests in isolation. The caller may
 * pass a translated `label` and/or a custom `onBack`.
 */
import { ArrowLeft } from 'lucide-react';

function defaultBack() {
  if (typeof window === 'undefined') return;
  // Prefer returning to the previous page (e.g. Settings); fall back to the
  // app root for deep-links opened directly with no history.
  if (window.history && window.history.length > 1) window.history.back();
  else window.location.assign('/');
}

export default function BackToAppButton({ label = 'Back to app', onBack }) {
  return (
    <button
      type="button"
      onClick={onBack || defaultBack}
      data-testid="back-to-app"
      aria-label={label}
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
        left: 'calc(env(safe-area-inset-left, 0px) + 10px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', gap: 6,
        minHeight: 44, padding: '8px 14px',
        borderRadius: 10, border: 'none', cursor: 'pointer',
        background: 'var(--navy-surface)', color: 'var(--inv)',
        fontSize: 13, fontWeight: 600,
        boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
      }}
    >
      <ArrowLeft size={16} strokeWidth={2} /> {label}
    </button>
  );
}
